/**
 * TSV Discovery Collection PDP handler
 *
 * Loaded by sections/tsv-product-discovery.liquid. Lives at
 * assets/tsv-product-discovery.js.
 *
 * Responsibilities:
 *   1. Gallery — clicking a thumbnail swaps the main image
 *   2. Qty stepper (− / +)
 *   3. Gift options panel toggle
 *   4. Gift add-on checkboxes (wrap, bag, card)
 *   5. AJAX chained Add to Bag — main product + each selected add-on
 *      via /cart/add.js, with gift line item properties merged into the
 *      main product line
 *   6. Get-it-by date calculator — Europe/Dublin time, before-noon cutoff,
 *      next-working-day delivery in Ireland, 2–6 working days for EU
 *   7. Fires `tsv:cart:added` on document so the custom cart drawer can
 *      open + refresh
 */
(function () {
  // Locale lookup. Strings come from locales/*.json via snippets/tsv-i18n-js.liquid.
  function T(key, fallback) { return (window.TSV_T && window.TSV_T[key]) || fallback; }

  'use strict';

  function init(root) {
    if (!root || root.__tsvPdpInit) return;
    root.__tsvPdpInit = true;

    initGallery(root);
    initQty(root);
    initGiftToggle(root);
    initGiftAddons(root);
    initAtc(root);
    initGetItBy(root);
    initStickyHook(root);
    initFragTitleSplit(root);
  }

  /* ───── Title splitter for fragrance cards ─────
     Same logic as tsv-discovery.js / the regular PDP. Turns
     "Dior Sauvage EDP 100ml" into "Dior Sauvage" + subtitle
     "EAU DE PARFUM · 100ML". Applied to each .tsv-pdp__inside-card. */
  var TSV_CONC_MAP = {
    'edp': 'Eau de Parfum',
    'edt': 'Eau de Toilette',
    'edc': 'Eau de Cologne',
    'eds': 'Eau de Senteur',
    'parfum': 'Parfum',
    'elixir': 'Elixir',
    'extrait': 'Extrait',
    'cologne': 'Cologne'
  };
  var TSV_CONC_ABBR_RE = /\b(EDP|EDT|EDC|EDS)\b(?=(?:\s*(?:limited\s+edition|\(?\s*\d+(?:\.\d+)?\s*(?:[x×]\s*\d+(?:\.\d+)?\s*)?ml\s*\)?|\+\s*\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?\s*ml|(?:gift|discovery|travel)\s+set|duo|set))*\s*$)/i;
  var TSV_CONC_FULL_RE = /\b(Eau\s+de\s+Parfum(?:\s+Intense|\s+Fra[iî]che)?|Eau\s+de\s+Toilette(?:\s+Intense)?|Eau\s+de\s+Cologne|Eau\s+de\s+Senteur|Eau\s+Fra[iî]che|Extrait\s+de\s+Parfum|Parfum|Elixir|Extrait|Cologne)\b(?=(?:\s*(?:limited\s+edition|\(?\s*\d+(?:\.\d+)?\s*(?:[x×]\s*\d+(?:\.\d+)?\s*)?ml\s*\)?|\+\s*\d+(?:\.\d+)?\s*[x×]\s*\d+(?:\.\d+)?\s*ml|(?:gift|discovery|travel)\s+set|duo|set))*\s*$)/i;
  var TSV_SIZE_RE = /\(?\s*(\d+(?:\.\d+)?(?:\s*[x×]\s*\d+(?:\.\d+)?)?)\s*ml\b\s*\)?/i;

  function initFragTitleSplit(root) {
    root.querySelectorAll('.tsv-pdp__inside-card').forEach(function (card) {
      var titleEl = card.querySelector('[data-tsv-frag-title]');
      var subEl = card.querySelector('[data-tsv-frag-sub]');
      if (!titleEl || !subEl) return;
      var titleText = (titleEl.textContent || '').replace(/\s+/g, ' ').trim();
      if (!titleText) return;
      var name = titleText;
      var parts = [];
      var concMatch = titleText.match(TSV_CONC_ABBR_RE) || titleText.match(TSV_CONC_FULL_RE);
      if (concMatch) {
        var idx = concMatch.index;
        name = titleText.slice(0, idx).replace(/[\s,·\-–]+$/, '').trim();
        var rest = titleText.slice(idx + concMatch[0].length).trim();
        parts.push(TSV_CONC_MAP[concMatch[1].toLowerCase()] || concMatch[1]);
        // Strip the size from rest, but DON'T add it to the subtitle.
        // Customers are getting 3ml decants, so the source bottle size is
        // irrelevant and confusing here.
        rest = rest.replace(TSV_SIZE_RE, '').trim();
        rest = rest.replace(/\b(EDP|EDT|EDC|EDS|Parfum|Elixir|Extrait|Cologne|Eau\s+de\s+\w+)\b/ig, '');
        rest = rest.replace(/[\s,·\-–]+/g, ' ').trim();
        if (rest) parts.push(rest);
        if (!name) { name = titleText; parts = []; }
      } else {
        // No concentration — just strip any size from the name.
        var sm = titleText.match(TSV_SIZE_RE);
        if (sm) {
          name = titleText.replace(TSV_SIZE_RE, '').replace(/[\s,·\-–]+$/, '').trim();
        }
      }
      titleEl.textContent = name || titleText;
      var subtitle = parts.join(' · ');
      if (subtitle) {
        subEl.textContent = subtitle;
        subEl.hidden = false;
      }
    });
  }

  /* ───── Sticky ATC bar hook ─────
     The global tsv-app-enhancements snippet renders a sticky bottom bar
     (#tsv-sticky-atc) on every product page and tries to submit the
     standard Shopify product form on click. This PDP doesn't use that
     form, so we intercept clicks on the sticky button (capture phase) and
     trigger our own ATC button instead — gift options + chained add-ons
     stay intact. The IntersectionObserver that tracks `.tsv-atc` for
     show/hide visibility works unchanged because we added .tsv-atc to
     the real ATC button. */
  function initStickyHook(root) {
    var stickyBtn = document.getElementById('tsv-sticky-btn');
    var realBtn = root.querySelector('[data-tsv-pdp-atc]');
    if (!stickyBtn || !realBtn) return;
    stickyBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      realBtn.click();
    }, true);
  }

  /* ───── Gallery ───── */
  function initGallery(root) {
    var mainImg = root.querySelector('[data-tsv-pdp-main-img]');
    var thumbs = root.querySelectorAll('[data-tsv-pdp-thumb]');
    if (!mainImg || !thumbs.length) return;
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-large');
        if (!src) return;
        mainImg.setAttribute('src', src);
        mainImg.removeAttribute('srcset');
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  }

  /* ───── Qty ───── */
  function initQty(root) {
    var input = root.querySelector('[data-tsv-pdp-qty]');
    var minus = root.querySelector('[data-tsv-pdp-qty-minus]');
    var plus = root.querySelector('[data-tsv-pdp-qty-plus]');
    if (!input) return;
    function step(d) {
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < 1) v = 1;
      input.value = Math.max(1, v + d);
    }
    if (minus) minus.addEventListener('click', function () { step(-1); });
    if (plus) plus.addEventListener('click', function () { step(1); });
  }

  /* ───── Gift panel toggle ───── */
  function initGiftToggle(root) {
    var toggle = root.querySelector('[data-tsv-pdp-gift-toggle]');
    if (!toggle) return;
    toggle.addEventListener('click', function () {
      root.classList.toggle('is-gift-open');
    });
  }

  /* ───── Gift add-on checkboxes ─────
     Click toggles .is-checked on the label. Read by getSelectedAddons(). */
  function initGiftAddons(root) {
    var addons = root.querySelectorAll('[data-tsv-gift-addon]');
    addons.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        el.classList.toggle('is-checked');
      });
    });
  }

  function getSelectedAddons(root) {
    return Array.from(root.querySelectorAll('[data-tsv-gift-addon].is-checked'))
      .map(function (el) {
        return {
          handle: el.getAttribute('data-handle'),
          requiresMessage: el.getAttribute('data-requires-message') === '1',
        };
      });
  }

  /* ───── Add to Bag ─────
     Chains: main product (with gift properties) → each add-on.
     The card add-on receives the gift message as a line item property
     so the printer sees what to write. */
  function initAtc(root) {
    var btn = root.querySelector('[data-tsv-pdp-atc]');
    var err = root.querySelector('[data-tsv-pdp-err]');
    if (!btn) return;

    var variantId = root.getAttribute('data-variant-id');
    var productHandle = root.getAttribute('data-product-handle');
    var origLabel = btn.textContent.trim();

    btn.addEventListener('click', function () {
      hideError(err);
      submit();
    });

    function submit() {
      var qty = parseInt((root.querySelector('[data-tsv-pdp-qty]') || {}).value, 10) || 1;
      var giftOpen = root.classList.contains('is-gift-open');
      var recipientName = giftOpen
        ? String((root.querySelector('[name="_recipient_name"]') || {}).value || '').trim()
        : '';
      var giftMessage = giftOpen
        ? String((root.querySelector('[name="_gift_message"]') || {}).value || '').trim()
        : '';
      var addons = giftOpen ? getSelectedAddons(root) : [];

      // Validate: if "Personalised Card" is ticked, require a message.
      var cardAddon = addons.find(function (a) { return a.requiresMessage; });
      if (cardAddon && !giftMessage) {
        return showError(err, T('pdj_gift_message_required', 'Please write a gift message before adding the personalised card.'));
      }

      setLoading(true);

      // Build main product line item properties.
      var mainProps = {};
      if (giftOpen) {
        mainProps._is_gift = 'yes';
        if (recipientName) mainProps._recipient_name = recipientName;
        if (giftMessage) mainProps._gift_message = giftMessage;
        if (addons.length) {
          mainProps._gift_addons = addons.map(function (a) { return a.handle; }).join(', ');
        }
      }

      // 1. Add the main product
      addItem({ id: Number(variantId), quantity: qty, properties: mainProps })
        .then(function () {
          // 2. Chain the add-ons. Card line gets the message as a property.
          var chain = Promise.resolve();
          addons.forEach(function (a) {
            chain = chain.then(function () {
              return resolveVariantId(a.handle).then(function (vId) {
                if (!vId) return;
                var props = { _addon_for: productHandle };
                if (a.requiresMessage && giftMessage) {
                  props._card_message = giftMessage;
                }
                return addItem({ id: vId, quantity: qty, properties: props });
              });
            });
          });
          return chain;
        })
        .then(function () {
          // Set cart attribute so the cart drawer + future pages know
          // this is a gift order. Doesn't block, fire-and-forget.
          if (giftOpen) {
            fetch('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                attributes: {
                  gift_order: 'yes',
                  gift_recipient: recipientName || '',
                },
              }),
            }).catch(function () {});
          }
          // Notify any theme listeners (cart icon bubble, points, etc.) that
          // the cart changed, but DO NOT open the cart drawer — keeps the
          // customer on the PDP so they can keep configuring.
          document.dispatchEvent(new CustomEvent('tsv:cart:added', {
            detail: { handle: productHandle, variantId: Number(variantId) },
          }));
          setLoading(false);
          btn.classList.add('is-added');
          btn.textContent = 'Added ✓';
          setTimeout(function () {
            btn.classList.remove('is-added');
            btn.textContent = origLabel;
          }, 1800);

          // Refresh cart count bubble in the header
          fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.querySelectorAll('.cart-count-bubble').forEach(function (b) {
                var span = b.querySelector('span[aria-hidden]') || b.querySelector('span');
                if (span) span.textContent = cart.item_count;
                b.style.display = cart.item_count > 0 ? '' : 'none';
              });
            })
            .catch(function () {});

          // Fire the existing global toast. Matches what the rest of the
          // site uses on every other product page.
          if (typeof window.tsvToast === 'function') {
            var title = ((root.querySelector('.tsv-pdp__title') || {}).textContent || 'Discovery Collection').trim();
            var subParts = [T('pdj_six_atomisers','6 × 3ml Atomisers')];
            if (addons.length) {
              subParts.push(addons.length === 1
                ? T('pdj_one_addon','1 gift add-on')
                : addons.length + T('pdj_addons_suffix',' gift add-ons'));
            }
            window.tsvToast({
              title: title,
              sub: subParts.join(' · '),
              action: T('pdj_view_bag','VIEW BAG'),
            });
          }
        })
        .catch(function (e) {
          setLoading(false);
          var msg = (e && e.description) || (e && e.message) || T('pdj_add_to_bag_failed', 'Couldn\'t add to bag. Please try again.');
          showError(err, msg);
          if (typeof window.tsvToast === 'function') {
            window.tsvToast({ title: T('pdj_add_failed','Could not add to bag'), sub: msg, error: true });
          }
        });
    }

    function setLoading(on) {
      btn.disabled = !!on;
      btn.classList.toggle('is-loading', !!on);
      if (on) btn.textContent = 'Adding…';
      else if (!btn.classList.contains('is-added')) btn.textContent = origLabel;
    }
  }

  function addItem(payload) {
    var formData = new FormData();
    formData.append('id', String(payload.id));
    formData.append('quantity', String(payload.quantity || 1));
    if (payload.properties) {
      Object.keys(payload.properties).forEach(function (k) {
        formData.append('properties[' + k + ']', payload.properties[k]);
      });
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) throw j;
        return j;
      });
    });
  }

  /* Cache variant lookups so chained adds for the same handle don't refetch. */
  var _variantCache = {};
  function resolveVariantId(handle) {
    if (!handle) return Promise.resolve(null);
    if (_variantCache[handle]) return Promise.resolve(_variantCache[handle]);
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      credentials: 'same-origin',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p || !Array.isArray(p.variants) || !p.variants.length) return null;
        var available = p.variants.find(function (v) { return v.available; }) || p.variants[0];
        _variantCache[handle] = available.id;
        return available.id;
      })
      .catch(function () { return null; });
  }

  function showError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }
  function hideError(el) {
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  /* ───── Get-it-by date ─────
     Logic:
       Cutoff = 12:00 Europe/Dublin
       Before cutoff on a weekday → ships TODAY → delivered next working day
       After cutoff OR weekend  → ships next working day → delivered the day after
       EU: ship date + 2 (earliest) to + 6 (latest) working days */
  function initGetItBy(root) {
    var irEl = root.querySelector('[data-tsv-pdp-getby-ireland]');
    var euEl = root.querySelector('[data-tsv-pdp-getby-eu]');
    if (!irEl && !euEl) return;

    function update() {
      var now = dublinNow();
      var isWeekend = now.getDay() === 0 || now.getDay() === 6;
      var beforeCutoff = (now.getHours() < 12) && !isWeekend;

      var shipDate = new Date(now);
      if (!beforeCutoff) {
        shipDate = nextWorkday(shipDate);
      }
      var ireDeliver = nextWorkday(shipDate);
      var euEarliest = addWorkdays(shipDate, 2);
      var euLatest = addWorkdays(shipDate, 6);

      // Always show a countdown — to the NEXT 12:00 workday cutoff,
      // whether that's later today or a future workday.
      var nextCutoff = new Date(now);
      nextCutoff.setHours(12, 0, 0, 0);
      if (now >= nextCutoff || isWeekend) {
        do {
          nextCutoff.setDate(nextCutoff.getDate() + 1);
          nextCutoff.setHours(12, 0, 0, 0);
        } while (nextCutoff.getDay() === 0 || nextCutoff.getDay() === 6);
      }
      var countdownHtml = '<span class="tsv-pdp__getby-countdown">' +
        formatCountdown(nextCutoff - now) + '</span>';

      if (irEl) {
        irEl.innerHTML =
          T('pdj_order_in','Order in ') + countdownHtml + ' &middot; ' +
          'Ships <strong>' + formatDay(shipDate) + '</strong> &middot; ' +
          'Get it <strong>' + formatDay(ireDeliver) + '</strong>';
      }
      if (euEl) {
        euEl.innerHTML =
          T('pdj_order_in','Order in ') + countdownHtml + ' &middot; ' +
          'Ships <strong>' + formatDay(shipDate) + '</strong> &middot; ' +
          'Get it <strong>' + formatDay(euEarliest) + ' to ' + formatDay(euLatest) + '</strong>';
      }
    }
    update();
    // Refresh every minute so the countdown ticks down.
    setInterval(update, 60 * 1000);
  }

  function formatCountdown(ms) {
    var totalMin = Math.max(0, Math.floor(ms / 60000));
    var days = Math.floor(totalMin / 1440);
    var hours = Math.floor((totalMin % 1440) / 60);
    var mins = totalMin % 60;
    if (days > 0) {
      return days + 'd ' + hours + 'h ' + (mins < 10 ? '0' + mins : mins) + 'm';
    }
    if (hours > 0) {
      return hours + 'h ' + (mins < 10 ? '0' + mins : mins) + 'm';
    }
    return mins + 'm';
  }

  function formatLongDay(d) {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long',
      }).format(d);
    } catch (_) {
      return d.toDateString();
    }
  }

  function dublinNow() {
    // Approximate Dublin time using Intl to extract correct hours regardless
    // of the viewer's local timezone. We construct a Date in their local
    // timezone using the y/m/d/h/m/s parts as if they were Dublin's.
    try {
      var fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Dublin',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
      });
      var parts = fmt.formatToParts(new Date());
      var get = function (t) { return Number((parts.find(function (p) { return p.type === t; }) || {}).value || 0); };
      return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
    } catch (_) {
      return new Date();
    }
  }

  function nextWorkday(d) {
    var x = new Date(d.getTime());
    do {
      x.setDate(x.getDate() + 1);
    } while (x.getDay() === 0 || x.getDay() === 6);
    return x;
  }
  function addWorkdays(d, n) {
    var x = new Date(d.getTime());
    var added = 0;
    while (added < n) {
      x.setDate(x.getDate() + 1);
      if (x.getDay() !== 0 && x.getDay() !== 6) added++;
    }
    return x;
  }
  function formatDay(d) {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        weekday: 'short', day: '2-digit', month: 'short',
      }).format(d);
    } catch (_) {
      return d.toDateString();
    }
  }

  function boot() {
    document.querySelectorAll('[data-tsv-pdp]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else { boot(); }
  document.addEventListener('shopify:section:load', boot);
})();